<template>
	<DialogBox
		v-if="isOpen"
		:size="styles?.size"
		:is-full-size="isFullSize"
		:is-simple="isSimple"
		:is-bottom-sheet="isBottomSheet"
		:borderless="styles?.borderless"
		:empty-dialog="styles?.emptyDialog"
		:interactable-background="body?.interactableBackground"
		:close-on-backdrop-click="body?.closeOnBackdropClick"
		@cancel="onClose"
	>
		<template #header v-if="header">
			<DialogHeader :title="header.title" :closable="header.closable" @cancel="onClose" />
		</template>
		<DialogBody
			:borderless="styles?.borderless"
			:is-bottom-sheet="isBottomSheet"
			:bottom-sheet-type="styles?.bottomSheetType"
		>
			<component
				v-if="component"
				:class="{ 'height-full': isFullSize }"
				:is="component"
				v-bind="componentProps"
				@accept="onAccept"
				@close="onClose"
				@cancel="onClose"
			>
			</component>
			<p v-if="body?.text" v-html="body.text"></p>
		</DialogBody>
		<template #actions v-if="actions?.accept || actions?.cancel">
			<DialogActions
				:accept-action="actions?.accept"
				:cancel-action="actions?.cancel"
				:invert="actions.invert"
				@accept="onAccept"
				@cancel="onClose"
			/>
		</template>
	</DialogBox>
</template>

<script setup lang="ts">
	import { computed, markRaw, onMounted, onUnmounted, ref, shallowRef, toRaw } from 'vue'
	import DialogBox from './DialogBox.vue'
	import DialogHeader from './DialogHeader.vue'
	import DialogBody from './DialogBody.vue'
	import DialogActions from './DialogActions.vue'
	import EventsService from '~/services/events'
	import {
		DialogBodyConfig,
		DialogConfig,
		DialogHeaderConfig,
		DialogStyles,
		DialogActionsConfig,
	} from './dialogTypes'
	import { maxSizes } from '~/models/Responsive'

	const isOpen = ref<boolean>(false)

	const component = shallowRef<any>()
	const componentProps = ref<Record<string, any>>()
	const data = ref<Record<string, any>>()

	const body = ref<DialogBodyConfig>()
	const styles = ref<DialogStyles>()
	const actions = ref<DialogActionsConfig>()
	const header = ref<DialogHeaderConfig>()

	const acceptEvent = ref<string>()
	const closeEvent = ref<string>()

	const originalOverflow = ref<string>('auto')

	const isXS = ref(window.innerWidth <= maxSizes.xs)

	const isSimple = computed(() => {
		return !isXS.value || styles.value?.mobileType === 'simple'
	})
	const isFullSize = computed(() => {
		return (styles.value?.mobileType === 'fullScreen' && isXS.value) || false
	})
	const isBottomSheet = computed(() => {
		return (styles.value?.mobileType === 'bottomSheet' && isXS.value) || false
	})

	onMounted(() => {
		listenOpenDialog()
		originalOverflow.value = document.body.style.overflow
		window.addEventListener('resize', handleResize)
	})

	onUnmounted(() => {
		window.removeEventListener('resize', handleResize)
	})

	const listenOpenDialog = () => {
		EventsService.listenOpenDialog(open)
		EventsService.listenCloseDialog(close)
	}

	const open = (event: DialogConfig) => {
		resetProperties()
		setBody(event)
		setActions(event)
		setConfig(event)
		setData(event)
		document.body.style.setProperty('overflow', 'hidden')
		isOpen.value = true
	}

	const close = () => {
		isOpen.value = false
		document.body.style.setProperty('overflow', originalOverflow.value)
	}

	const onClose = (message: any) => {
		let emitResult: any = message
		EventsService.emit(closeEvent.value as any, emitResult)
		close()
	}

	const onAccept = (message: any) => {
		let emitResult: any = message
		if (data.value && !message) emitResult = data.value
		EventsService.emit(acceptEvent.value as any, emitResult)
		if (actions.value?.closeOnAccept) close()
	}

	const setBody = (event: DialogConfig) => {
		component.value = event.body?.component ? markRaw(toRaw(event.body.component)) : undefined
		componentProps.value = event.body?.componentProps
		body.value = event.body
	}

	const setActions = (event: DialogConfig) => {
		acceptEvent.value = event.acceptEvent
		closeEvent.value = event.closeEvent
		actions.value = event.actions
	}

	const setConfig = (event: DialogConfig) => {
		styles.value = event.styles
		header.value = event.header
	}

	const setData = (event: DialogConfig) => {
		data.value = event.data
	}

	const resetProperties = () => {
		component.value = undefined
		componentProps.value = undefined
		acceptEvent.value = undefined
		closeEvent.value = undefined
		data.value = undefined
		header.value = undefined
		styles.value = undefined
		actions.value = undefined
		body.value = undefined
	}

	const handleResize = () => {
		isXS.value = window.innerWidth <= maxSizes.xs
	}
</script>
